import React, { useEffect, useState } from "react"
import Client from "../../models/client"
import useSWR, { useSWRConfig } from "swr"
import { FETCH_KEYS, fetchPageGroup, updatePageGroup } from "../api"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, Loader, NumberInput, Radio, ScrollArea, Select, Stack, Text, TextInput } from "@mantine/core"
import AceTextEditor from "./AceTextEditor"
import { DateTimePicker } from "@mantine/dates"
import { confirmDiscardChanges, confirmUnpublishPage } from "../utils/confirm"

interface Props {
  opened: boolean
  onClose: () => void
  client: Client
  pageGroupId: number | null
  onSaved?: () => void
}

interface FormValues {
  name: string
  pageDescription: string
  bodyHtml: string
  startDate: string
  endDate: string
  enabled: string
  guest_limit: number | string
}

const TEST_SWITCH_TEMPLATE = `<html>
<% if @rid == 'test' %>
<!-- テストするCMSのタグを貼ってください。 -->
<% else %>
<!-- 公開中のCMSのタグを貼ってください。 -->
<% end %>
</html>`

const defaultValues: FormValues = {
  name: 'invite',
  pageDescription: '',
  bodyHtml: '',
  startDate: '',
  endDate: '',
  enabled: '0',
  guest_limit: 0,
}

const PageGroupFormModal: React.FC<Props> = ({
  opened,
  onClose,
  client,
  pageGroupId,
  onSaved,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const { mutate } = useSWRConfig()

  const pageGroupKey =
    opened && pageGroupId !== null
      ? FETCH_KEYS.pageGroup(client.id, pageGroupId)
      : null

  const { data, isLoading } = useSWR(
    pageGroupKey,
    () => fetchPageGroup(client.id, pageGroupId as number),
  )

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      pageDescription: (v) =>
        !v ? '必須です' : v.length > 255 ? '255文字以下で入力してください' : null,
      startDate: (v) => (!v ? '必須です' : null),
      endDate: (v) => (!v ? '必須です' : null),
      guest_limit: (v, values) => {
        if (values.name !== 'guest') return null
        if (v === '' || v === null || v === undefined) return '必須です'
        if (isNaN(Number(v))) return '数字で入力してください'
        if (Number(v) < 0) return 'マイナスの数字は入力できません'
        return null
      },
    },
  })

  useEffect(() => {
    if (!opened) return
    if (data) {
      form.setValues({
        name: data.name,
        pageDescription: data.page_description ?? '',
        bodyHtml: data.body_html ?? '',
        // オフセット（+09:00 等）を解釈するとブラウザのタイムゾーンに変換され、
        // JST以外の端末で日時がずれてしまうため、旧実装と同様にオフセットを切り捨てて
        // サーバーが返す壁時計の値をそのまま表示する
        startDate: data.start_date ? data.start_date.slice(0, 19).replace('T', ' ') : '',
        endDate: data.end_date ? data.end_date.slice(0, 19).replace('T', ' ') : '',
        enabled: String(Number(data.enabled)),
        guest_limit: Number(data.guest_limit ?? 0),
      })
      form.resetDirty()
    }
  }, [opened, data])

  const handleInsertTestTemplate = () => {
    if (form.values.bodyHtml && !window.confirm('本文を上書きします。よろしいですか？')) {
      return
    }
    form.setFieldValue('bodyHtml', TEST_SWITCH_TEMPLATE)
  }

  const handleClose = () => {
    if (
      form.isDirty() &&
      !confirmDiscardChanges()
    ) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    const willBeEnabled = values.enabled === '1'
    if (pageGroupId === null) return
    const wasEnabled = Number(data?.enabled) === 1
    if (
      wasEnabled &&
      !willBeEnabled &&
      !confirmUnpublishPage()
    ) {
      return
    }

    setSubmitting(true)
    try {
      const pageGroup = {
        name: values.name,
        page_description: values.pageDescription,
        enabled: values.enabled,
        body_html: values.bodyHtml,
        start_date: values.startDate,
        end_date: values.endDate,
        guest_limit: values.guest_limit,
      }
      // この画面は画像を編集しないため画像パラメータは渡さない（渡すと既存画像が消える）
      await updatePageGroup(client.id, pageGroupId, pageGroup)
      // 開きなおしたときに古い値が出ないよう、この page_groups のキャッシュも更新する
      await mutate(pageGroupKey)
      notifications.show({ message: 'ページを更新しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (e: any) {
      console.error('ページの保存に失敗しました', e.response?.status, e.response?.data)
      const errData = e.response?.data
      const msg = errData?.errors?.join('\n') ?? errData?.error ?? '保存に失敗しました'
      notifications.show({ message: msg, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  const showLoader = pageGroupId !== null && (isLoading || !data)

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="ページ編集"
      width={1000}
      size="85vh"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      {showLoader ? (
        <Group justify="center" py="xl" style={{ width: '100%' }}>
          <Loader />
        </Group>
      ) : (
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          onKeyDown={(e) => {
            // 入力欄でのEnterによる暗黙送信を止める（textareaの改行は維持）
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
            <Stack gap="md" p="md">
              <Select
                label="種類"
                data={[
                  { value: 'invite', label: 'invite' },
                  { value: 'invite2', label: 'invite2' },
                  { value: 'guest', label: 'guest' },
                ]}
                allowDeselect={false}
                disabled={Number(data?.enabled) === 1}
                {...form.getInputProps('name')}
              />

              <TextInput
                label={<><span>概要</span>{requiredMark}</>}
                styles={requireLabelStyles}
                {...form.getInputProps('pageDescription')}
              />

              {form.values.name === 'guest' && (
                <NumberInput
                  label={<><span>1回のシェアで紹介できる人数（0は無制限）</span>{requiredMark}</>}
                  styles={requireLabelStyles}
                  min={0}
                  allowNegative={false}
                  {...form.getInputProps('guest_limit')}
                />
              )}

              <Box>
                <Text size="sm" fw={500} mb={8}>HTML本体</Text>
                <Box bg="#fff" p="sm" style={{ borderRadius: 8, border: '1px solid #0000001A' }}>
                  <AceTextEditor
                    input={{
                      value: form.values.bodyHtml,
                      onChange: (v: string) => form.setFieldValue('bodyHtml', v),
                      onFocus: () => { },
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Button
                  variant="outline"
                  radius="xl"
                  type="button"
                  onClick={handleInsertTestTemplate}
                >
                  テスト切替テンプレートを挿入
                </Button>
              </Box>

              <DateTimePicker
                label={<><span>開始日時</span>{requiredMark}</>}
                styles={requireLabelStyles}
                valueFormat="YYYY-MM-DD HH:mm"
                value={form.values.startDate || null}
                onChange={(v) => form.setFieldValue('startDate', v ?? '')}
                error={form.errors.startDate}
                clearable
                locale="ja"
              />

              <DateTimePicker
                label={<><span>終了日時</span>{requiredMark}</>}
                styles={requireLabelStyles}
                valueFormat="YYYY-MM-DD HH:mm"
                value={form.values.endDate || null}
                onChange={(v) => form.setFieldValue('endDate', v ?? '')}
                error={form.errors.endDate}
                clearable
                locale="ja"
              />

              <Radio.Group label="ステータス" {...form.getInputProps('enabled')}>
                <Group mt="xs">
                  <Radio value="0" label="非公開" />
                  <Radio value="1" label="本番" />
                </Group>
              </Radio.Group>
            </Stack>
          </ScrollArea>

          <Box
            p="md"
            style={{ borderTop: '1px solid #0000001A', backgroundColor: '#fff', flexShrink: 0 }}
          >
            <Group justify="flex-end" gap={8}>
              <Button
                variant="filled"
                radius="xl"
                type="submit"
                loading={submitting}
              >保存</Button>
            </Group>
          </Box>
        </form>
      )}
    </BaseModal>
  )
}

export default PageGroupFormModal
